import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, AlertTriangle, Truck as TruckIcon, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StockTransferForm } from './StockTransferForm';
import { StockTransferFormValues } from './schema';
import { 
  getStockTransferOrder, 
  createStockTransferOrder, 
  updateStockTransferOrder,
  updateStockTransferOrderStatus,
  checkStockAvailability
} from '@/app/services/stockTransferService';
import { listGodowns } from '@/app/services/inventoryService';
import { StockTransferStatus, getStatusColor, getStatusLabel, getNextStatuses } from '@/app/types/stockTransfer';
import { format } from 'date-fns';

const InternalTransfersDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isEditMode = !!id;
  
  // Check if we're coming from Low Stock Report
  const locationState = location.state as {
    fromLowStockReport?: boolean;
    prefilledItems?: any[];
    sourceContext?: string;
  } | null;
  const isFromLowStockReport = locationState?.fromLowStockReport || false;
  const prefilledItems = locationState?.prefilledItems || [];
  
  // State for status update dialog
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<StockTransferStatus | null>(null);
  const [statusNotes, setStatusNotes] = useState('');
  
  // State for item search
  const [isSearchingItems, setIsSearchingItems] = useState(false);

  // Fetch stock transfer order if in edit mode
  const { 
    data: stockTransfer, 
    isLoading: isLoadingTransfer, 
    isError: isErrorTransfer,
    error: errorTransfer
  } = useQuery({
    queryKey: ['stock-transfer-order', id],
    queryFn: () => getStockTransferOrder(Number(id)),
    enabled: isEditMode,
  });

  // Check if the order can be edited
  const canEdit = !stockTransfer || stockTransfer.status === 'draft';
  const canUpdateStatus = stockTransfer && getNextStatuses(stockTransfer.status).length > 0;

  // Fetch godowns for selection
  const { data: godownsData = { items: [] }, isLoading: isLoadingGodowns } = useQuery({
    queryKey: ['godowns'],
    queryFn: () => listGodowns({ limit: 100 })
  });

  // Create stock transfer mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => createStockTransferOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfer-orders'] });
      toast.success('Stock transfer order created successfully');
      navigate('/items/internal-transfers');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create stock transfer order: ${error.message}`);
    },
  });

  // Update stock transfer mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => updateStockTransferOrder(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfer-orders'] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfer-order', id] });
      toast.success('Stock transfer order updated successfully');
      navigate('/items/internal-transfers');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update stock transfer order: ${error.message}`);
    },
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ status, notes }: { status: StockTransferStatus; notes: string }) => 
      updateStockTransferOrderStatus(Number(id), status, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfer-orders'] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfer-order', id] });
      toast.success('Status updated successfully');
      setIsStatusDialogOpen(false);
      setSelectedStatus(null);
      setStatusNotes('');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update status: ${error.message}`);
    },
  });

  // Handle form submission
  const handleSubmit = (values: StockTransferFormValues) => {
    if (isEditMode && id) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  };

  // Handle status update
  const handleStatusUpdate = (newStatus: StockTransferStatus) => {
    setSelectedStatus(newStatus);
    setIsStatusDialogOpen(true);
  };

  const confirmStatusUpdate = () => {
    if (selectedStatus) {
      updateStatusMutation.mutate({ status: selectedStatus, notes: statusNotes });
    }
  };

  // Check stock availability for an item
  const checkItemStock = async (itemId: number, godownId: number) => {
    try {
      const availability = await checkStockAvailability(itemId, godownId);
      return availability.quantity;
    } catch (error) {
      console.error('Failed to check stock:', error);
      return 0;
    }
  };

  // Status badge component
  const StatusBadge: React.FC<{ status: StockTransferStatus }> = ({ status }) => {
    const color = getStatusColor(status);
    const label = getStatusLabel(status);
    
    const variantMap: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      gray: 'secondary',
      blue: 'default',
      green: 'outline',
      red: 'destructive',
    };
    
    return (
      <Badge variant={variantMap[color] || 'default'} className="font-medium">
        {label}
      </Badge>
    );
  };

  // Loading state
  if (isEditMode && isLoadingTransfer) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (isEditMode && isErrorTransfer) {
    return (
      <div className="container mx-auto py-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading stock transfer order: {(errorTransfer as Error)?.message || 'An unknown error occurred'}
          </AlertDescription>
        </Alert>
        <Button
          variant="outline"
          onClick={() => navigate('/items/internal-transfers')}
          className="mt-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to List
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/items/internal-transfers')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">
              {isEditMode ? 'Stock Transfer Order' : 'New Stock Transfer Order'}
            </h1>
            {stockTransfer && (
              <div className="flex items-center gap-4 mt-2">
                <span className="text-sm text-muted-foreground">
                  {stockTransfer.document_number}
                </span>
                <StatusBadge status={stockTransfer.status} />
                <span className="text-sm text-muted-foreground">
                  Created: {format(new Date(stockTransfer.created_at), 'dd/MM/yyyy HH:mm')}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Status Actions */}
        {canUpdateStatus && (
          <div className="flex gap-2">
            {getNextStatuses(stockTransfer.status).map((status) => {
              let buttonVariant: 'default' | 'destructive' | 'outline' = 'outline';
              let icon = null;
              
              if (status === 'in_transit') {
                buttonVariant = 'default';
                icon = <TruckIcon className="h-4 w-4 mr-2" />;
              } else if (status === 'completed') {
                buttonVariant = 'outline';
                icon = <CheckCircle className="h-4 w-4 mr-2" />;
              } else if (status === 'cancelled') {
                buttonVariant = 'destructive';
                icon = <XCircle className="h-4 w-4 mr-2" />;
              }
              
              return (
                <Button
                  key={status}
                  variant={buttonVariant}
                  onClick={() => handleStatusUpdate(status)}
                  disabled={updateStatusMutation.isPending}
                >
                  {icon}
                  {getStatusLabel(status)}
                </Button>
              );
            })}
          </div>
        )}
      </div>

      {/* Warning for non-editable status */}
      {isEditMode && !canEdit && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This transfer order is in {getStatusLabel(stockTransfer.status)} status and cannot be edited.
            Only draft orders can be modified.
          </AlertDescription>
        </Alert>
      )}

      {/* Info for Low Stock Report context */}
      {isFromLowStockReport && !isEditMode && (
        <Alert>
          <AlertTriangle className="h-4 w-4 text-warning" />
          <AlertDescription>
            This transfer order has been pre-filled with low stock items from the Low Stock Report.
            Please review the suggested quantities and source/destination godowns before saving.
          </AlertDescription>
        </Alert>
      )}

      {/* Form */}
      {isLoadingGodowns ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading form data...</span>
        </div>
      ) : (
        <StockTransferForm
          initialData={stockTransfer}
          godowns={godownsData.items}
          onSubmit={handleSubmit}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
          disabled={!canEdit}
          checkItemStock={checkItemStock}
          prefilledItems={isFromLowStockReport ? prefilledItems : undefined}
        />
      )}

      {/* Status Update Confirmation Dialog */}
      <AlertDialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Transfer Status</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change the status to {selectedStatus && getStatusLabel(selectedStatus)}?
              {selectedStatus === 'in_transit' && (
                <span className="block mt-2 font-semibold">
                  This will deduct stock from the source godown and add it to the destination godown.
                </span>
              )}
              {selectedStatus === 'cancelled' && stockTransfer?.status === 'in_transit' && (
                <span className="block mt-2 font-semibold text-destructive">
                  This will reverse the stock movement and restore original quantities.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <label className="text-sm font-medium">Notes (optional)</label>
            <textarea
              className="w-full mt-1 p-2 border rounded-md"
              rows={3}
              placeholder="Add notes about this status change..."
              value={statusNotes}
              onChange={(e) => setStatusNotes(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setSelectedStatus(null);
              setStatusNotes('');
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmStatusUpdate}
              disabled={updateStatusMutation.isPending}
            >
              {updateStatusMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Confirm'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InternalTransfersDetailPage;